// @flow

import { useEffect, useRef } from 'react';
import { useTenant, type TenantCode } from '@tectonics/commons/tenant';
import { usePayment, type PaymentAction } from '../context';
import { queryHostedPayment } from '../services/payment-api';
import { serverDeadline } from './waiting-deadline';

export const POLLING_INTERVAL_MS = 5000;
export const PAYMENT_REJECTED_MESSAGE = 'El pago fue rechazado. Inténtalo de nuevo.';

const paymentRejectedAction: PaymentAction = {
  type: 'SET_ERROR',
  errorMessage: PAYMENT_REJECTED_MESSAGE,
  retryState: 'LOADING',
};
const paymentConfirmedAction: PaymentAction = { type: 'PAYMENT_CONFIRMED' };

type PollingLoop = {
  cancelled: boolean,
  timerId: ?TimeoutID,
  deadline: number,
};

type PollingContext = {|
  +paymentHash: string,
  +tenantCode: TenantCode,
  +dispatch: (action: PaymentAction) => void,
  +loop: PollingLoop,
|};

async function poll(context: PollingContext): Promise<void> {
  const { paymentHash, tenantCode, dispatch, loop } = context;

  if (loop.cancelled) return;

  if (Date.now() >= loop.deadline) {
    dispatch({ type: 'SET_TIMEOUT' });
    return;
  }

  try {
    const response = await queryHostedPayment(paymentHash, tenantCode);

    if (loop.cancelled) return;

    const nextDeadline = serverDeadline(response.expires_in_seconds);
    if (nextDeadline != null) {
      const deadlineChanged = Math.abs(nextDeadline - loop.deadline) > 1000;
      if (deadlineChanged) {
        loop.deadline = nextDeadline;
        dispatch({
          type: 'SET_WAITING_DEADLINE',
          waitingDeadline: nextDeadline,
        });
      }
    }

    if (response.payment_status === 'CO') {
      dispatch(paymentConfirmedAction);
      return;
    }

    if (response.payment_status === 'CA') {
      dispatch(paymentRejectedAction);
      return;
    }
  } catch (_error) {
    if (loop.cancelled) return;
  }

  loop.timerId = setTimeout(() => {
    poll(context);
  }, POLLING_INTERVAL_MS);
}

export function useApprovalPolling() {
  const tenant = useTenant();
  const { data, dispatch } = usePayment();
  const waitingDeadlineRef = useRef<?number>(null);
  waitingDeadlineRef.current = data.waitingDeadline;
  const hasWaitingDeadline = data.waitingDeadline != null;

  useEffect(() => {
    if (
      (data.state !== 'WAITING_APPROVAL'
        && data.state !== 'AWAITING_PAYMENT_STATUS')
      || !hasWaitingDeadline
    ) {
      return;
    }

    const waitingDeadline = waitingDeadlineRef.current;
    if (waitingDeadline == null) return;

    const loop: PollingLoop = {
      cancelled: false,
      timerId: null,
      deadline: waitingDeadline,
    };

    poll({
      paymentHash: data.paymentHash,
      tenantCode: tenant.code,
      dispatch,
      loop,
    });

    return () => {
      loop.cancelled = true;
      if (loop.timerId) {
        clearTimeout(loop.timerId);
        loop.timerId = null;
      }
    };
  }, [data.state, data.paymentHash, hasWaitingDeadline, tenant.code, dispatch]);
}
